import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

const TONE = { active: "brand", used: "success", revoked: "error", expired: "neutral" };

function Pick({ id, label, value, onChange, options }) {
  return (
    <div><Label htmlFor={id} className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="mt-1 bg-white capitalize" data-testid={`${id}-select`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All</SelectItem>{options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export function CodesTable({ items, onRevoke, testId = "invite-codes-table" }) {
  if (!items?.length) return <Empty title="No codes match" testId={`${testId}-empty`} />;
  return (
    <div className="overflow-x-auto"><table className="w-full min-w-[820px] text-left text-sm" data-testid={testId}>
      <thead><tr className="cw-muted border-b border-border text-xs"><th className="py-3 pl-5 font-medium">Code</th><th className="py-3 pr-4 font-medium">Source</th><th className="py-3 pr-4 font-medium">Status</th><th className="py-3 pr-4 font-medium">Used by</th><th className="py-3 pr-4 font-medium">Expires</th><th className="py-3 pr-5" /></tr></thead>
      <tbody>{items.map((c) => (
        <tr key={c.id} className="border-b border-border last:border-0" data-testid={`invite-code-row-${c.id}`}>
          <td className="py-3 pl-5 font-mono font-medium">{c.code}</td>
          <td className="py-3 pr-4"><span className="capitalize">{c.kind}</span><span className="cw-muted block truncate text-xs">{c.email || (c.owner_user_id && <Link to={`/admin/users/${c.owner_user_id}`} className="hover:underline">{c.owner_user_id}</Link>) || "—"}</span></td>
          <td className="py-3 pr-4"><Pill tone={TONE[c.status]} className="capitalize" data-testid={`invite-code-status-${c.id}`}>{c.status}</Pill></td>
          <td className="py-3 pr-4 text-xs">{c.used_by && c.used_by !== "deleted" ? <Link to={`/admin/users/${c.used_by}`} className="font-mono hover:underline">{c.used_by}</Link> : c.used_by || "—"}{c.used_at && <span className="cw-muted block">{formatDate(c.used_at)}</span>}</td>
          <td className="cw-muted whitespace-nowrap py-3 pr-4 text-xs">{c.expires_at ? formatDate(c.expires_at) : "Never"}</td>
          <td className="py-3 pr-5 text-right">{onRevoke && c.status === "active" && <Button size="sm" variant="outline" onClick={() => onRevoke(c)} data-testid={`invite-code-revoke-button-${c.id}`}>Revoke</Button>}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

export default function InviteCodesTab() {
  const [f, setF] = useState({ kind: "all", status: "all", q: "" });
  const [q, setQ] = useState("");
  const [revoking, setRevoking] = useState(null);
  const qs = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => v && v !== "all" && qs.set(k, v));
  const { data, error, loading, reload } = useAdminLoad(`/admin/invite-codes?${qs}`);
  const revoke = async () => { await api(`/admin/invite-codes/${revoking.id}/revoke`, { method: "POST" }); toast.success(`${revoking.code} revoked.`); reload(); };

  return (
    <div className="space-y-4" data-testid="launch-codes-tab">
      <form onSubmit={(e) => { e.preventDefault(); setF((s) => ({ ...s, q: q.trim().toUpperCase() })); }} className="grid gap-3 sm:grid-cols-[160px_160px_1fr_auto] sm:items-end">
        <Pick id="codes-kind" label="Kind" value={f.kind} onChange={(v) => setF((s) => ({ ...s, kind: v }))} options={["wave", "referral"]} />
        <Pick id="codes-status" label="Status" value={f.status} onChange={(v) => setF((s) => ({ ...s, status: v }))} options={["active", "used", "revoked", "expired"]} />
        <div><Label htmlFor="codes-q" className="text-xs">Code</Label><Input id="codes-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="CW…" className="mt-1 bg-white font-mono" data-testid="codes-q-input" /></div>
        <Button type="submit" data-testid="codes-search-button">Search</Button>
      </form>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="invite-codes-total">{data?.total ?? 0} codes</p>
          <CodesTable items={data?.items} onRevoke={setRevoking} />
        </Panel>
      </LoadState>
      <ConfirmDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)} testId="code-revoke-confirm-dialog" onConfirm={revoke}
        title={`Revoke ${revoking?.code}?`} confirmLabel="Revoke code" description="It stops working right away. Accounts already created with it are not affected." />
    </div>
  );
}
